import { RenderBalanceInColor } from '@/components/features/accounts/account-utils'
import { CashBoxMovementsTable } from '@/components/features/accounts/CashBoxMovementsTable'
import DatePicker from '@/components/features/shared/DatePicker'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { downloadCsv, formatNumEsAr } from '@/components/features/shared/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { SelectItem } from '@/components/ui/select'
import { SelectField } from '@/components/ui/select-field'
import { Switch } from '@/components/ui/switch'
import { CashBoxMovement, formatMovementType, MovementType } from '@/domain/accounts/account'
import { formatPaymentMethodShortName, PaymentMethod } from '@/domain/payments/payments'
import { useCashBoxMovements } from '@/hooks/api/useAccounts'
import { useDateRange } from '@/hooks/useDateRange'
import { useFrontendPagination } from '@/hooks/useFrontendPagination'
import { format } from 'date-fns'
import { CreditCard, Download, TrendingDown, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { SummaryExtended } from '@/components/features/shared/SummaryExtended'
import { PageContainer } from '@/components/features/shared/PageContainer'

const exportCashBoxMovements = (movements: CashBoxMovement[], dateFrom: string, dateTo: string) => {
  const headers = ['Fecha', 'Hora', 'Tipo', 'Descripcion', 'Cliente', 'Denominación', 'Metodo de Pago', 'Monto', 'Saldo']
  const rows = movements.map(m => {
    const date = new Date(m.movementDate)
    const fecha = format(date, 'dd/MM/yyyy')
    const hora = format(date, 'HH:mm:ss')
    const tipo = formatMovementType(m.movementType)
    const descripcion = `"${(m.description || '').replace(/"/g, '""')}"`
    const monto = m.movementType === MovementType.Credit ? m.amount : -m.amount
    const saldo = m.balanceAfter
    const metodo = formatPaymentMethodShortName(m.paymentMethod)
    const cliente = m.customerCode || 'N/A'
    const denominacion = m.customerName || 'N/A'
    return [fecha, hora, tipo, descripcion, cliente, denominacion, metodo, formatNumEsAr(monto), formatNumEsAr(saldo)].join(';')
  })

  const paymentMethodTotals = new Map<string, { credits: number; debits: number }>()
  const allMethods = new Set<string>()

  movements.forEach(m => {
    const metodo = formatPaymentMethodShortName(m.paymentMethod)
    allMethods.add(metodo)
    const existing = paymentMethodTotals.get(metodo) || { credits: 0, debits: 0 }
    if (m.movementType === MovementType.Credit) {
      existing.credits += m.amount
    } else {
      existing.debits += m.amount
    }
    paymentMethodTotals.set(metodo, existing)
  })

  const summaryRows: string[] = []
  const sortedMethods = Array.from(allMethods).sort()
  for (const metodo of sortedMethods) {
    const totals = paymentMethodTotals.get(metodo)!
    const balance = totals.credits - totals.debits
    summaryRows.push(`"";"";"";"";"RESUMEN POR METODO DE PAGO";"";"";"";"";""`)
    summaryRows.push(`"";"";"";"";"Descripcion";"Balance";"Creditos";"Debitos";"";""`)
    summaryRows.push(`"";"";"";"";"${metodo}";"${formatNumEsAr(balance)}";"${formatNumEsAr(totals.credits)}";"${formatNumEsAr(totals.debits)}";"";""`)
    summaryRows.push('')
  }

  const totalCredits = movements.filter(m => m.movementType === MovementType.Credit).reduce((sum, m) => sum + m.amount, 0)
  const totalDebits = movements.filter(m => m.movementType === MovementType.Debit).reduce((sum, m) => sum + m.amount, 0)
  const totalBalance = totalCredits - totalDebits

  summaryRows.push(`"";"";"";"";"RESUMEN GENERAL";"";"";"";"";""`)
  summaryRows.push(`"";"";"";"";"Descripcion";"Balance";"Creditos";"Debitos";"";""`)
  summaryRows.push(`"";"";"";"";"TOTAL";${formatNumEsAr(totalBalance)};${formatNumEsAr(totalCredits)};${formatNumEsAr(totalDebits)};"";""`)

  const csvContent = '\uFEFF' + [headers.join(';'), ...rows, ...summaryRows].join('\n')
  const today = format(new Date(), 'yyyy-MM-dd')
  downloadCsv(csvContent, `caja_${dateFrom}_${dateTo}_${today}.csv`)
}

interface CashBoxMovementsProps {
  userId?: string
}

export const CashBoxMovements = ({ userId }: CashBoxMovementsProps) => {
  const { fromDate, toDate, setFromDate, setToDate, dateFrom, dateTo } = useDateRange()

  const [includeCollectorAccounts, setIncludeCollectorAccounts] = useState(false)
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethod | undefined>(undefined)

  const { data: balanceData, isLoading: isLoadingBalance } = useCashBoxMovements(userId || '', {
    fromDate: fromDate?.toISOString(),
    toDate: toDate?.toISOString(),
    includeCollectors: includeCollectorAccounts,
    paymentMethod: paymentMethodFilter,
  })

  const movements = balanceData?.movements || []

  // console.log(movements)

  const { paginatedData, pagination, setPagination, pageCount, totalRecords } = useFrontendPagination(movements, {
    initialPageSize: 20,
    initialPageIndex: 0,
  })

  const handleExport = () => {
    exportCashBoxMovements(movements, dateFrom || '', dateTo || '')
  }

  return (
    <PageContainer>
      <NavHeader>
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">Balance de Caja</h1>
          <p className="text-sm text-primary font-medium flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Pagos registrados en el período seleccionado
          </p>
        </div>
      </NavHeader>

      <Card>
        <CardContent className='pt-6'>
          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="date-from" className="text-xs">Desde</Label>
              <DatePicker
                date={fromDate ?? new Date()}
                onSelect={setFromDate}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="date-to" className="text-xs">Hasta</Label>
              <DatePicker
                date={toDate ?? new Date()}
                onSelect={setToDate}
              />
            </div>
            <div className="flex flex-col gap-1 w-48">
              <Label htmlFor="payment-method">Método de Pago</Label>
              <SelectField
                value={paymentMethodFilter ?? ''}
                onValueChange={(value) => setPaymentMethodFilter(value === 'all' ? undefined : value as PaymentMethod)}
                placeholder="Todos"
              >
                <SelectItem value={'all'}>Todos</SelectItem>
                <SelectItem value={PaymentMethod.CASH}>Efectivo</SelectItem>
                <SelectItem value={PaymentMethod.CREDIT_CARD}>Tja Crédito</SelectItem>
                <SelectItem value={PaymentMethod.DEBIT_CARD}>Tja Débito</SelectItem>
                <SelectItem value={PaymentMethod.BANK_TRANSFER}>Transferencia</SelectItem>
                <SelectItem value={PaymentMethod.OTHER}>Otro</SelectItem>
              </SelectField>
            </div>
            <div className="flex flex-col items-start gap-2">
              <Label htmlFor="collector-accounts">Cobradores</Label>
              <Switch checked={includeCollectorAccounts}
                className='mb-2'
                onCheckedChange={(checked) => setIncludeCollectorAccounts(checked === true)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <SummaryExtended
        items={[
          {
            title: 'Total ingresos en el período',
            value: <RenderBalanceInColor balance={balanceData?.totalCredits || 0} />,
            icon: TrendingUp,
            subtitle: `Movimientos: ${movements.filter(p => p.movementType === MovementType.Credit).length}`,
            subtitleClassName: `text-sm font-semibold text-muted-foreground ${isLoadingBalance ? 'animate-pulse' : ''}`,
            valueClassName: isLoadingBalance ? 'animate-pulse' : '',
          },
          {
            title: 'Total egresos en el período',
            value: <RenderBalanceInColor balance={(balanceData?.totalDebits || 0)} />,
            icon: TrendingDown,
            subtitle: `Movimientos: ${movements.filter(p => p.movementType === MovementType.Debit).length}`,
            subtitleClassName: `text-sm font-semibold text-muted-foreground ${isLoadingBalance ? 'animate-pulse' : ''}`,
            valueClassName: isLoadingBalance ? 'animate-pulse' : '',
          },
          {
            title: 'Saldo del período',
            value: <RenderBalanceInColor balance={balanceData?.balance || 0} />,
            icon: CreditCard,
            valueClassName: isLoadingBalance ? 'animate-pulse' : '',
          },
        ]}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Movimientos</CardTitle>
              <CardDescription>
                {totalRecords} movimiento{totalRecords !== 1 ? 's' : ''} en el período
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isLoadingBalance || movements.length === 0}
            >
              <Download className="h-4 w-4 mr-1.5" />
              Exportar CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <CashBoxMovementsTable
            paginated={{
              data: paginatedData,
              total: totalRecords,
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              totalPages: pageCount,
            }}
            isLoading={isLoadingBalance}
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
